<template>
  <Teleport to="body">
    <Transition name="drawer">
      <div v-if="modelValue" class="app-drawer-overlay" :class="{ 'no-overlay': noOverlay, 'is-left': side === 'left', 'is-bottom': side === 'bottom' }" @click.self="!noOverlay && close()">
        <div class="app-drawer" :class="sizeClass">
          <!-- Header -->
          <div class="drawer-header">
            <div style="min-width: 0; flex: 1;">
              <slot name="header">
                <h3 class="title is-5 mb-0">{{ title }}</h3>
                <p v-if="subtitle" class="subtitle is-7 mb-0 mt-1 has-text-grey text-ellipsis">
                  {{ subtitle }}
                </p>
              </slot>
            </div>
            <button
              v-if="!hideCloseButton && quiet"
              type="button"
              class="drawer-close-btn"
              title="Close (Esc)"
              aria-label="Close"
              @click="close"
            >
              <kbd>ESC</kbd>
            </button>
            <button v-else-if="!hideCloseButton" class="delete is-medium" @click="close" aria-label="Close"></button>
          </div>

          <!-- Body -->
          <div class="drawer-body">
            <slot></slot>
          </div>

          <!-- Footer -->
          <div v-if="$slots.footer || !hideFooter" class="drawer-footer">
            <slot name="footer">
              <button class="button is-text" @click="close">Close</button>
            </slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large', 'xlarge'].includes(value)
  },
  side: {
    type: String,
    default: 'right',
    validator: (value) => ['left', 'right', 'bottom'].includes(value)
  },
  noOverlay: {
    type: Boolean,
    default: false
  },
  hideFooter: {
    type: Boolean,
    default: false
  },
  hideCloseButton: {
    type: Boolean,
    default: false
  },
  closeOnEsc: {
    type: Boolean,
    default: true
  },
  // ESC keycap instead of the X, as in AppModal's quiet header.
  quiet: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'close'])

const sizeClass = computed(() => {
  const sizeMap = {
    small: 'is-small',
    medium: '',
    large: 'is-large',
    xlarge: 'is-xlarge'
  }
  return sizeMap[props.size] || ''
})

const close = () => {
  emit('update:modelValue', false)
  emit('close')
}

const onKeydown = (e) => {
  if (e.key === 'Escape' && props.modelValue && props.closeOnEsc) {
    close()
  }
}

watch(() => props.modelValue, (isOpen) => {
  if (props.noOverlay) return
  if (isOpen) {
    document.body.classList.add('drawer-open')
  } else {
    document.body.classList.remove('drawer-open')
  }
})

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  document.body.classList.remove('drawer-open')
})
</script>

<style lang="scss" scoped>
.app-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10002;
  display: flex;
  justify-content: flex-end;

  &.is-left {
    justify-content: flex-start;

    .app-drawer {
      box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
    }
  }

  // Slides up from the bottom, near full height — for a workspace, not a form.
  &.is-bottom {
    align-items: flex-end;

    .app-drawer {
      width: 100%;
      height: 92vh;
      border-radius: 12px 12px 0 0;
      box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
    }
  }

  &.no-overlay {
    background: transparent;
    pointer-events: none;

    .app-drawer {
      pointer-events: all;
    }
  }
}

.app-drawer {
  width: 480px;
  max-width: 100vw;
  height: 100%;
  background: var(--bulma-scheme-main, #fff);
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);

  &.is-small {
    width: 360px;
  }

  &.is-large {
    width: 640px;
  }

  &.is-xlarge {
    width: 860px;
  }
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--bulma-border, #dbdbdb);
  flex-shrink: 0;
}

.drawer-close-btn {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  kbd {
    display: inline-block;
    padding: 0.15rem 0.4rem;
    border: 1px solid var(--bulma-border);
    border-bottom-width: 2px;
    border-radius: var(--bulma-radius-small);
    background: var(--bulma-scheme-main);
    font-family: inherit;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--bulma-text-weak);
    transition: color 0.15s, border-color 0.15s;
  }

  &:hover kbd {
    color: var(--bulma-text-strong);
    border-color: var(--bulma-text-weak);
  }
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--bulma-border, #dbdbdb);
  flex-shrink: 0;
}

.text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Transition
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.2s ease;

  .app-drawer {
    transition: transform 0.25s ease;
  }
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;

  .app-drawer {
    transform: translateX(100%);
  }

  &.is-left .app-drawer {
    transform: translateX(-100%);
  }

  &.is-bottom .app-drawer {
    transform: translateY(100%);
  }
}

.no-overlay.drawer-enter-from,
.no-overlay.drawer-leave-to {
  opacity: 1;
}

// Prevent body scroll when drawer is open
:global(body.drawer-open) {
  overflow: hidden;
}
</style>
